/**
 * Seller Redux Slice
 * Manages state for seller portal functionality
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import sellerApiService from '../../services/sellerApiService';

// ==================== Async Thunks ====================

// Profile
export const fetchSellerProfile = createAsyncThunk(
  'seller/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getProfile();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Products
export const fetchProducts = createAsyncThunk(
  'seller/fetchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const normalizedParams = { ...(params || {}) };

      // Normalize status: 'active' -> true, 'inactive' -> false, 'all' -> remove
      if (normalizedParams.status === 'active') {
        normalizedParams.status = true;
      } else if (normalizedParams.status === 'inactive') {
        normalizedParams.status = false;
      } else if (normalizedParams.status === 'all') {
        delete normalizedParams.status;
      }

      const hasSearch = normalizedParams?.search && normalizedParams.search.trim();
      const hasStatusFilter =
        typeof normalizedParams.status === 'boolean';

      // Use search endpoint whenever there's a text search OR a status filter
      if (hasSearch || hasStatusFilter) {
        return await sellerApiService.searchProducts(normalizedParams);
      }

      // Plain list (no search, no status filter) uses list endpoint
      return await sellerApiService.getProducts(normalizedParams);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProductStats = createAsyncThunk(
  'seller/fetchProductStats',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getProductStats();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'seller/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      // Normalize status for API: 'active'/'inactive' -> boolean
      const normalized = { ...(productData || {}) };
      if (normalized.status === 'active') {
        normalized.status = true;
      } else if (normalized.status === 'inactive') {
        normalized.status = false;
      }

      return await sellerApiService.createProduct(normalized);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'seller/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const normalized = { ...(productData || {}) };
      if (normalized.status === 'active') {
        normalized.status = true;
      } else if (normalized.status === 'inactive') {
        normalized.status = false;
      }

      return await sellerApiService.updateProduct(productId, normalized);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'seller/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      await sellerApiService.deleteProduct(productId);
      return productId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Campaigns
export const fetchCampaigns = createAsyncThunk(
  'seller/fetchCampaigns',
  async (params, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCampaigns(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCampaign = createAsyncThunk(
  'seller/createCampaign',
  async (campaignData, { rejectWithValue }) => {
    try {
      return await sellerApiService.createCampaign(campaignData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'seller/updateCampaign',
  async ({ campaignId, campaignData }, { rejectWithValue }) => {
    try {
      return await sellerApiService.updateCampaign(campaignId, campaignData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCampaign = createAsyncThunk(
  'seller/deleteCampaign',
  async (campaignId, { rejectWithValue }) => {
    try {
      await sellerApiService.deleteCampaign(campaignId);
      return campaignId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCampaignStats = createAsyncThunk(
  'seller/fetchCampaignStats',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCampaignsStats();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateCampaign = createAsyncThunk(
  'seller/activateCampaign',
  async (campaignId, { rejectWithValue }) => {
    try {
      return await sellerApiService.activateCampaign(campaignId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const endCampaign = createAsyncThunk(
  'seller/endCampaign',
  async (campaignId, { rejectWithValue }) => {
    try {
      return await sellerApiService.endCampaign(campaignId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAccountCampaigns = createAsyncThunk(
  'seller/fetchAccountCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await sellerApiService.getAccountCampaigns();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Customers
export const fetchCustomers = createAsyncThunk(
  'seller/fetchCustomers',
  async (params, { rejectWithValue }) => {
    try {
      const normalized = { ...(params || {}) };

      const rawSearch =
        typeof normalized.search === 'string' ? normalized.search : '';
      const trimmedSearch = rawSearch.trim();
      const hasSearch = trimmedSearch.length >= 2;

      // Tier filter should always use the search endpoint as well
      const hasTierFilter =
        typeof normalized.tier === 'string' && normalized.tier.trim() !== '' && normalized.tier !== 'all';

      if (hasSearch) {
        normalized.query = trimmedSearch;
      }
      // Never send raw search param to the API
      delete normalized.search;

      if (hasSearch || hasTierFilter) {
        return await sellerApiService.searchCustomers(normalized);
      }

      return await sellerApiService.getCustomers(normalized);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerDetails = createAsyncThunk(
  'seller/fetchCustomerDetails',
  async (customerId, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCustomer(customerId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerStats = createAsyncThunk(
  'seller/fetchCustomerStats',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCustomerStats();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerTransactions = createAsyncThunk(
  'seller/fetchCustomerTransactions',
  async ({ customerId, params = {} }, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCustomerTransactions(customerId, params);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Earning Rules
export const fetchEarningRules = createAsyncThunk(
  'seller/fetchEarningRules',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getEarningRules();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Redemption Rules
export const fetchRedemptionRules = createAsyncThunk(
  'seller/fetchRedemptionRules',
  async (_, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      return await sellerApiService.getRedemptionRules();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createRedemptionRule = createAsyncThunk(
  'seller/createRedemptionRule',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      return await sellerApiService.createRedemptionRule(payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateRedemptionRule = createAsyncThunk(
  'seller/updateRedemptionRule',
  async ({ ruleId, ...payload }, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      return await sellerApiService.updateRedemptionRule(ruleId, payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const closeRedemptionRule = createAsyncThunk(
  'seller/closeRedemptionRule',
  async (ruleId, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      await sellerApiService.closeRedemptionRule(ruleId);
      return ruleId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteRedemptionRule = createAsyncThunk(
  'seller/deleteRedemptionRule',
  async (ruleId, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      await sellerApiService.deleteRedemptionRule(ruleId);
      return ruleId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRedemptionRulesHistory = createAsyncThunk(
  'seller/fetchRedemptionRulesHistory',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      return await sellerApiService.getHistory(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Rewards (mirrors cashiers: normalize params, search/filter → searchRewards else getRewards)
export const fetchRewards = createAsyncThunk(
  'seller/fetchRewards',
  async (params, { rejectWithValue }) => {
    try {
      const normalized = { ...(params || {}) };
      const rawSearch = typeof normalized.search === 'string' ? normalized.search : '';
      const trimmedSearch = rawSearch.trim();
      const hasSearch = trimmedSearch.length >= 2;
      const hasStatusFilter =
        typeof normalized.status === 'string' &&
        normalized.status.trim() !== '' &&
        normalized.status !== 'all';
      const hasTypeFilter =
        typeof normalized.type === 'string' &&
        normalized.type.trim() !== '' &&
        normalized.type !== 'all';

      if (hasSearch) {
        normalized.search = trimmedSearch;
      }

      if (hasSearch || hasStatusFilter || hasTypeFilter) {
        return await sellerApiService.searchRewards(normalized);
      }

      return await sellerApiService.getRewards(normalized);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createReward = createAsyncThunk(
  'seller/createReward',
  async (rewardData, { rejectWithValue }) => {
    try {
      return await sellerApiService.createReward(rewardData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateRewardStatus = createAsyncThunk(
  'seller/updateRewardStatus',
  async ({ rewardId, status }, { rejectWithValue }) => {
    try {
      return await sellerApiService.updateRewardStatus(rewardId, status);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateReward = createAsyncThunk(
  'seller/updateReward',
  async ({ rewardId, rewardData }, { rejectWithValue }) => {
    try {
      return await sellerApiService.updateReward(rewardId, rewardData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteReward = createAsyncThunk(
  'seller/deleteReward',
  async (rewardId, { rejectWithValue }) => {
    try {
      await sellerApiService.deleteReward(rewardId);
      return rewardId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Cashiers (mirrors fetchCustomers: normalize params, search/filter → searchCashiers else getCashiers)
export const fetchCashiers = createAsyncThunk(
  'seller/fetchCashiers',
  async (params, { rejectWithValue }) => {
    try {
      const normalized = { ...(params || {}) };
      const rawSearch = typeof normalized.search === 'string' ? normalized.search : '';
      const trimmedSearch = rawSearch.trim();
      const hasSearch = trimmedSearch.length >= 2;
      const hasFilter =
        typeof normalized.status === 'string' && normalized.status.trim() !== '' && normalized.status !== 'all';

      if (hasSearch) {
        normalized.search = trimmedSearch;
      }
      if (hasSearch || hasFilter) {
        return await sellerApiService.searchCashiers(normalized);
      }
      return await sellerApiService.getCashiers(normalized);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCashierStats = createAsyncThunk(
  'seller/fetchCashierStats',
  async (_, { rejectWithValue }) => {
    try {
      return await sellerApiService.getCashierStats();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCashier = createAsyncThunk(
  'seller/createCashier',
  async (cashierData, { rejectWithValue }) => {
    try {
      return await sellerApiService.createCashier(cashierData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCashier = createAsyncThunk(
  'seller/updateCashier',
  async ({ cashierId, cashierData }, { rejectWithValue }) => {
    try {
      return await sellerApiService.updateCashier(cashierId, cashierData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCashier = createAsyncThunk(
  'seller/deleteCashier',
  async (cashierId, { getState, rejectWithValue }) => {
    try {
      const user = getState()?.loyalty?.admin?.user;
      const sellerId = user?.seller_ids?.[0];
      if (!sellerId) return rejectWithValue({ message: 'Seller context required' });
      await sellerApiService.deleteCashier(sellerId, cashierId);
      return cashierId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCashierStatus = createAsyncThunk(
  'seller/updateCashierStatus',
  async ({ cashierId, status }, { rejectWithValue }) => {
    try {
      return await sellerApiService.updateCashierStatus(cashierId, status);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Dashboard Stats
export const fetchDashboardStats = createAsyncThunk(
  'seller/fetchDashboardStats',
  async (params, { rejectWithValue }) => {
    try {
      return await sellerApiService.getDashboardStats(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Initial State ====================

const initialState = {
  // Profile
  profile: null,
  profileLoading: false,
  profileError: null,

  // Products
  products: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  productsLoading: false,
  productsError: null,
  productStats: null,
  productStatsLoading: false,
  productStatsError: null,

  // Campaigns
  campaigns: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  campaignsLoading: false,
  campaignsError: null,
  campaignStats: null,
  campaignStatsLoading: false,
  campaignStatsError: null,

  accountCampaigns: {
    data: [],
    hide_tab: false,
    is_read_only: true,
    banner_text: '',
    account_type: null
  },
  accountCampaignsLoading: false,
  accountCampaignsError: null,

  // Customers
  customers: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  customersLoading: false,
  customersError: null,
  selectedCustomer: null,
  customerStats: null,
  customerStatsLoading: false,
  customerStatsError: null,
  transactionsLoading: false,
  transactionsError: null,

  // Earning Rules
  earningRules: [],
  earningRulesLoading: false,
  earningRulesError: null,

  // Redemption Rules
  redemptionRules: [],
  redemptionRulesLoading: false,
  redemptionRulesError: null,
  redemptionRulesHistory: [],
  redemptionRulesHistoryLoading: false,
  redemptionRulesHistoryError: null,
  // Task 5.4 — which implementation served the history ('derived' | 'audit'),
  // the server-side total (audit mode paginates on the server) and any C1
  // diagnostic returned instead of a silent empty list.
  redemptionRulesHistorySource: 'derived',
  redemptionRulesHistoryTotal: 0,
  redemptionRulesHistoryDiagnostic: null,

  // Rewards
  rewards: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  rewardsLoading: false,
  rewardsError: null,

  // Cashiers
  cashiers: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  },
  cashiersLoading: false,
  cashiersError: null,
  cashierStats: null,
  cashierStatsLoading: false,
  cashierStatsError: null,

  // Dashboard
  dashboardStats: null,
  dashboardLoading: false,
  dashboardError: null,
};

// ==================== Slice ====================

const sellerSlice = createSlice({
  name: 'seller',
  initialState,
  reducers: {
    // Clear errors
    clearError: (state, action) => {
      const errorType = action.payload;
      if (errorType) {
        state[`${errorType}Error`] = null;
      }
    },

    // Clear selected customer
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null;
    },

    // Reset seller state
    resetSellerState: () => initialState,
  },
  extraReducers: (builder) => {
    // Profile
    builder
      .addCase(fetchSellerProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchSellerProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        // Unwrap API response: actual profile data is in action.payload.data
        state.profile = action.payload?.data || action.payload;
      })
      .addCase(fetchSellerProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      });

    // Products - Fetch
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        const payload = action.payload;
        const responseData = payload?.data || payload;
        
        // Handle new structure: { items: [...], pagination: {...} }
        const productsArray = responseData?.items || responseData?.data || responseData;
        state.products.data = Array.isArray(productsArray) ? productsArray : [];

        // Support pagination object in response
        const pagination = responseData?.pagination;
        if (pagination) {
          state.products.pagination = {
            currentPage: pagination.page || 1,
            totalPages: pagination.totalPages || 1,
            totalItems: pagination.total || 0,
            itemsPerPage: pagination.limit || 10,
          };
        } else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          // Fallback to old structure
          state.products.pagination = {
            currentPage: responseData.page || state.products.pagination.currentPage || 1,
            totalPages: responseData.totalPages || state.products.pagination.totalPages || 1,
            totalItems: responseData.total || state.products.pagination.totalItems || 0,
            itemsPerPage: responseData.limit || state.products.pagination.itemsPerPage || 10,
          };
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      });

    // Products - Create
    builder
      .addCase(createProduct.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products.data.unshift(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      });

    // Products - Update
    builder
      .addCase(updateProduct.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.productsLoading = false;
        const index = state.products.data.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.products.data[index] = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      });

    // Products - Delete
    builder
      .addCase(deleteProduct.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products.data = state.products.data.filter(p => p.id !== action.payload);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      });

    // Product Stats
    builder
      .addCase(fetchProductStats.pending, (state) => {
        state.productStatsLoading = true;
        state.productStatsError = null;
      })
      .addCase(fetchProductStats.fulfilled, (state, action) => {
        state.productStatsLoading = false;
        const payload = action.payload;
        state.productStats = payload?.data || payload || null;
      })
      .addCase(fetchProductStats.rejected, (state, action) => {
        state.productStatsLoading = false;
        state.productStatsError = action.payload;
      });

    // Campaigns - Fetch
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.campaignsLoading = true;
        state.campaignsError = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        const res = action.payload?.data ?? action.payload;
        state.campaigns.data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.campaigns) ? res.campaigns : []);
        state.campaigns.pagination = {
          totalItems: res?.total ?? 0,
          currentPage: res?.page ?? 1,
          itemsPerPage: res?.limit ?? 10,
          totalPages: res?.totalPages ?? 1
        };
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.campaignsError = action.payload;
      });

    // Campaigns - Create
    builder
      .addCase(createCampaign.pending, (state) => {
        state.campaignsLoading = true;
        state.campaignsError = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        if (!Array.isArray(state.campaigns.data)) {
          state.campaigns.data = [];
        }
        if (action.payload && typeof action.payload === 'object' && action.payload.id) {
          state.campaigns.data.unshift(action.payload);
        }
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.campaignsError = action.payload;
      });

    // Campaigns - Update
    builder
      .addCase(updateCampaign.pending, (state) => {
        state.campaignsLoading = true;
        state.campaignsError = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        if (!Array.isArray(state.campaigns.data)) {
          state.campaigns.data = [];
        }
        const index = state.campaigns.data.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.campaigns.data[index] = action.payload;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.campaignsError = action.payload;
      });

    // Campaigns - Delete
    builder
      .addCase(deleteCampaign.pending, (state) => {
        state.campaignsLoading = true;
        state.campaignsError = null;
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        if (!Array.isArray(state.campaigns.data)) {
          state.campaigns.data = [];
        } else {
          state.campaigns.data = state.campaigns.data.filter(c => c.id !== action.payload);
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.campaignsError = action.payload;
      });

    builder
      .addCase(fetchCampaignStats.pending, (state) => {
        state.campaignStatsLoading = true;
        state.campaignStatsError = null;
      })
      .addCase(fetchCampaignStats.fulfilled, (state, action) => {
        state.campaignStatsLoading = false;
        const payload = action.payload;
        state.campaignStats = payload?.data ?? payload ?? null;
      })
      .addCase(activateCampaign.fulfilled, (state, action) => {
        if (action.payload?.id && Array.isArray(state.campaigns.data)) {
          const idx = state.campaigns.data.findIndex(c => c.id === action.payload.id);
          if (idx !== -1) state.campaigns.data[idx] = action.payload;
        }
      })
      .addCase(endCampaign.fulfilled, (state, action) => {
        if (action.payload?.id && Array.isArray(state.campaigns.data)) {
          const idx = state.campaigns.data.findIndex(c => c.id === action.payload.id);
          if (idx !== -1) state.campaigns.data[idx] = action.payload;
        }
      })
      .addCase(fetchCampaignStats.rejected, (state, action) => {
        state.campaignStatsLoading = false;
        state.campaignStatsError = action.payload;
      })

    // Account Campaigns
    builder
      .addCase(fetchAccountCampaigns.pending, (state) => {
        state.accountCampaignsLoading = true;
        state.accountCampaignsError = null;
      })
      .addCase(fetchAccountCampaigns.fulfilled, (state, action) => {
        state.accountCampaignsLoading = false;
        const payload = action.payload;
        const res = payload?.data ?? payload;
        state.accountCampaigns = {
          data: Array.isArray(res?.data) ? res.data : [],
          hide_tab: !!res?.hide_tab,
          is_read_only: res?.is_read_only !== false,
          banner_text: res?.banner_text || '',
          account_type: res?.account_type || null
        };
      })
      .addCase(fetchAccountCampaigns.rejected, (state, action) => {
        state.accountCampaignsLoading = false;
        state.accountCampaignsError = action.payload;
      });;

    // Customers - Fetch
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.customersLoading = true;
        state.customersError = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customersLoading = false;
        const payload = action.payload;
        const responseData = payload?.data || payload;
        const customersArray = responseData?.data || responseData;

        state.customers.data = Array.isArray(customersArray) ? customersArray : [];

        if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          state.customers.pagination = {
            currentPage: responseData.page || 1,
            totalPages: responseData.totalPages || 1,
            totalItems: responseData.total || 0,
            itemsPerPage: responseData.limit || 10,
          };
        } else if (payload?.pagination) {
          state.customers.pagination = payload.pagination;
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.customersLoading = false;
        state.customersError = action.payload;
      });

    // Customer Details
    builder
      .addCase(fetchCustomerDetails.pending, (state) => {
        state.customersLoading = true;
        state.customersError = null;
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.customersLoading = false;
        const payload = action.payload;
        state.selectedCustomer = payload?.data || payload || null;
      })
      .addCase(fetchCustomerDetails.rejected, (state, action) => {
        state.customersLoading = false;
        state.customersError = action.payload;
      });

    // Customer Stats
    builder
      .addCase(fetchCustomerStats.pending, (state) => {
        state.customerStatsLoading = true;
        state.customerStatsError = null;
      })
      .addCase(fetchCustomerStats.fulfilled, (state, action) => {
        state.customerStatsLoading = false;
        const payload = action.payload;
        state.customerStats = payload?.data || payload || null;
      })
      .addCase(fetchCustomerStats.rejected, (state, action) => {
        state.customerStatsLoading = false;
        state.customerStatsError = action.payload;
      });

    // Customer Transactions
    builder
      .addCase(fetchCustomerTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.transactionsError = null;
      })
      .addCase(fetchCustomerTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        const response = action.payload;
        // API returns { data: { data: [...], total, page, totalPages, limit, offset } }
        const result = response?.data || {};
        const transactions = result.data || [];
        if (state.selectedCustomer) {
          state.selectedCustomer.transactions = transactions;
          // Mark as fetched even when empty ([]) so the UI does not refetch
          // forever for customers with no transaction history.
          state.selectedCustomer.transactionsLoaded = true;
          // Store pagination metadata for UI
          state.selectedCustomer.transactionsPagination = {
            total: result.total || 0,
            page: result.page || 1,
            totalPages: result.totalPages || 1,
            limit: result.limit || 10
          };
        }
      })
      .addCase(fetchCustomerTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.transactionsError = action.payload;
      });

    // Earning Rules
    builder
      .addCase(fetchEarningRules.pending, (state) => {
        state.earningRulesLoading = true;
        state.earningRulesError = null;
      })
      .addCase(fetchEarningRules.fulfilled, (state, action) => {
        state.earningRulesLoading = false;
        state.earningRules = action.payload;
      })
      .addCase(fetchEarningRules.rejected, (state, action) => {
        state.earningRulesLoading = false;
        state.earningRulesError = action.payload;
      });

    // Redemption Rules
    builder
      .addCase(fetchRedemptionRules.pending, (state) => {
        state.redemptionRulesLoading = true;
        state.redemptionRulesError = null;
      })
      .addCase(fetchRedemptionRules.fulfilled, (state, action) => {
        state.redemptionRulesLoading = false;
        const payload = action.payload;
        const data = payload?.data ?? payload;
        
        // Parse response: can be {redemptionRules: {...}} or direct RedemptionSettings
        let raw = [];
        if (data?.redemptionRules) {
          // Global redemption mode: { redemptionRules: { reward_options: [...] } }
          const rr = data.redemptionRules;
          if (Array.isArray(rr.reward_options)) {
            raw = rr.reward_options.map(ro => ({
              ...ro,
              // flatten denominations from reward_options item
              reward_option: ro,
              validity_period_days: data.minimum_points || ro.validity_period_days || 30,
              cross_seller_redemption: data.cross_seller_redemption || false
            }));
          } else if (typeof rr === 'object') {
            raw = [rr];
          }
        } else if (data?.rules) {
          // Legacy format: { rules: [...] }
          raw = data.rules;
        } else if (Array.isArray(data)) {
          raw = data;
        } else if (data && typeof data === 'object' && (data.id != null || data.RewardOption || data.rewardOption || data.reward)) {
          // Direct RedemptionSettings object with RewardOption
          raw = [data];
        }
        
        const normalize = (r) => {
          // Extract reward option from capitalized Sequelize association or lowercase
          const rewardOption = r.reward_option ?? r.rewardOption ?? r.reward ?? r.RewardOption;
          return {
            ...r,
            reward_option: rewardOption,
            validity_period_days: r.validity_period_days ?? r.expired_period_days ?? r.validityPeriod ?? 30,
          };
        };
        state.redemptionRules = (raw || []).map(normalize);
      })
      .addCase(fetchRedemptionRules.rejected, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRulesError = action.payload;
      })
      .addCase(createRedemptionRule.pending, (state) => {
        state.redemptionRulesLoading = true;
        state.redemptionRulesError = null;
      })
      .addCase(createRedemptionRule.fulfilled, (state) => {
        state.redemptionRulesLoading = false;
      })
      .addCase(createRedemptionRule.rejected, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRulesError = action.payload;
      })
      .addCase(updateRedemptionRule.pending, (state) => {
        state.redemptionRulesLoading = true;
        state.redemptionRulesError = null;
      })
      .addCase(updateRedemptionRule.fulfilled, (state) => {
        state.redemptionRulesLoading = false;
      })
      .addCase(updateRedemptionRule.rejected, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRulesError = action.payload;
      })
      .addCase(closeRedemptionRule.pending, (state) => {
        state.redemptionRulesLoading = true;
        state.redemptionRulesError = null;
      })
      .addCase(closeRedemptionRule.fulfilled, (state) => {
        state.redemptionRulesLoading = false;
      })
      .addCase(closeRedemptionRule.rejected, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRulesError = action.payload;
      })
      .addCase(deleteRedemptionRule.pending, (state) => {
        state.redemptionRulesLoading = true;
        state.redemptionRulesError = null;
      })
      .addCase(deleteRedemptionRule.fulfilled, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRules = (state.redemptionRules || []).filter((r) => r.id !== action.payload);
      })
      .addCase(deleteRedemptionRule.rejected, (state, action) => {
        state.redemptionRulesLoading = false;
        state.redemptionRulesError = action.payload;
      })
      .addCase(fetchRedemptionRulesHistory.pending, (state) => {
        state.redemptionRulesHistoryLoading = true;
        state.redemptionRulesHistoryError = null;
      })
      .addCase(fetchRedemptionRulesHistory.fulfilled, (state, action) => {
        state.redemptionRulesHistoryLoading = false;
        const payload = action.payload;
        const data = payload?.data ?? payload;

        // Task 5.4 — the audit source returns an OBJECT ({ source, rows, count,
        // fallback, diagnostic }); the derived source still returns a bare ARRAY.
        // Both are normalised here so the component never has to branch on shape.
        if (data && !Array.isArray(data) && (data.source || Array.isArray(data.rows))) {
          const rows = Array.isArray(data.rows) ? data.rows : [];
          // When audit has nothing, prefer the server-provided derived fallback so
          // the card degrades to real history instead of a false empty state.
          const usable = rows.length > 0 ? rows : (Array.isArray(data.fallback) ? data.fallback : []);
          state.redemptionRulesHistory = usable;
          state.redemptionRulesHistorySource = usable === rows && rows.length > 0 ? 'audit' : (usable.length ? 'derived' : (data.source || 'audit'));
          state.redemptionRulesHistoryTotal = Number(data.count) || usable.length;
          state.redemptionRulesHistoryDiagnostic = data.diagnostic || null;
        } else {
          const raw = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : data?.items ?? [];
          state.redemptionRulesHistory = raw || [];
          state.redemptionRulesHistorySource = 'derived';
          state.redemptionRulesHistoryTotal = (raw || []).length;
          state.redemptionRulesHistoryDiagnostic = null;
        }
      })
      .addCase(fetchRedemptionRulesHistory.rejected, (state, action) => {
        state.redemptionRulesHistoryLoading = false;
        state.redemptionRulesHistoryError = action.payload;
      });

    // Rewards
    builder
      .addCase(fetchRewards.pending, (state) => {
        state.rewardsLoading = true;
        state.rewardsError = null;
      })
      .addCase(fetchRewards.fulfilled, (state, action) => {
        state.rewardsLoading = false;

        const payload = action.payload;
        const responseData = payload?.data || payload;

        // Normalize list array: backend returns { data: { pagination, items: [...] } }
        let items = [];
        if (Array.isArray(responseData?.items)) {
          items = responseData.items;
        } else if (Array.isArray(responseData?.data)) {
          items = responseData.data;
        } else if (Array.isArray(responseData)) {
          items = responseData;
        }

        // Normalize reward fields for table view
        state.rewards.data = items.map((item) => ({
          ...item,
          // Backend uses `type`; table expects `reward_type`
          reward_type: item.reward_type || item.type || null,
          // Backend uses `points` (array); table expects scalar `points_required`
          points_required:
            item.points_required ??
            (Array.isArray(item.points) ? item.points[0] : item.points),
        }));

        // Normalize pagination
        const p = responseData?.pagination;
        if (p && typeof p === 'object') {
          const itemsPerPage = p.limit || 10;
          const totalPages = p.total_pages || p.totalPages || 1;
          // Use total from backend if available, otherwise calculate
          const totalItems = p.total || p.total_items || p.totalItems || (totalPages * itemsPerPage);

          state.rewards.pagination = {
            currentPage: p.page || 1,
            totalPages,
            totalItems,
            itemsPerPage,
          };
        }
      })
      .addCase(fetchRewards.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.rewardsError = action.payload;
      })
      // Rewards - Create
      .addCase(createReward.pending, (state) => {
        state.rewardsLoading = true;
        state.rewardsError = null;
      })
      .addCase(createReward.fulfilled, (state) => {
        state.rewardsLoading = false;
      })
      .addCase(createReward.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.rewardsError = action.payload;
      })
      // Rewards - Update
      .addCase(updateReward.pending, (state) => {
        state.rewardsLoading = true;
        state.rewardsError = null;
      })
      .addCase(updateReward.fulfilled, (state) => {
        state.rewardsLoading = false;
      })
      .addCase(updateReward.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.rewardsError = action.payload;
      })
      // Rewards - Delete
      .addCase(deleteReward.pending, (state) => {
        state.rewardsLoading = true;
        state.rewardsError = null;
      })
      .addCase(deleteReward.fulfilled, (state) => {
        state.rewardsLoading = false;
      })
      .addCase(deleteReward.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.rewardsError = action.payload;
      })
      // Rewards - Update status (PATCH :reward_id/status)
      .addCase(updateRewardStatus.pending, (state) => {
        state.rewardsLoading = true;
        state.rewardsError = null;
      })
      .addCase(updateRewardStatus.fulfilled, (state) => {
        state.rewardsLoading = false;
      })
      .addCase(updateRewardStatus.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.rewardsError = action.payload;
      });

    // Cashiers - Fetch
    builder
      .addCase(fetchCashiers.pending, (state) => {
        state.cashiersLoading = true;
        state.cashiersError = null;
      })
      .addCase(fetchCashiers.fulfilled, (state, action) => {
        state.cashiersLoading = false;
        const payload = action.payload;
        const responseData = payload?.data || payload;
        const cashiersArray = responseData?.data || responseData;
        state.cashiers.data = Array.isArray(cashiersArray) ? cashiersArray : [];
        
        if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          state.cashiers.pagination = {
            currentPage: responseData.page || 1,
            totalPages: responseData.totalPages || 1,
            totalItems: responseData.total || 0,
            itemsPerPage: responseData.limit || 10,
          };
        } else if (payload?.pagination) {
          state.cashiers.pagination = payload.pagination;
        }
      })
      .addCase(fetchCashiers.rejected, (state, action) => {
        state.cashiersLoading = false;
        state.cashiersError = action.payload;
      });

    // Cashiers - Create
    builder
      .addCase(createCashier.pending, (state) => {
        state.cashiersLoading = true;
        state.cashiersError = null;
      })
      .addCase(createCashier.fulfilled, (state, action) => {
        state.cashiersLoading = false;
      })
      .addCase(createCashier.rejected, (state, action) => {
        state.cashiersLoading = false;
        state.cashiersError = action.payload;
      });

    // Cashiers - Update
    builder
      .addCase(updateCashier.pending, (state) => {
        state.cashiersLoading = true;
        state.cashiersError = null;
      })
      .addCase(updateCashier.fulfilled, (state, action) => {
        state.cashiersLoading = false;
        const cashier = action.payload?.data ?? action.payload;
        if (!cashier?.id) return;
        const index = state.cashiers.data.findIndex(c => c.id === cashier.id);
        if (index !== -1) {
          state.cashiers.data[index] = cashier;
        }
      })
      .addCase(updateCashier.rejected, (state, action) => {
        state.cashiersLoading = false;
        state.cashiersError = action.payload;
      });

    // Cashiers - Delete
    builder
      .addCase(deleteCashier.pending, (state) => {
        state.cashiersLoading = true;
        state.cashiersError = null;
      })
      .addCase(deleteCashier.fulfilled, (state, action) => {
        state.cashiersLoading = false;
        state.cashiers.data = state.cashiers.data.filter(c => c.id !== action.payload);
      })
      .addCase(deleteCashier.rejected, (state, action) => {
        state.cashiersLoading = false;
        state.cashiersError = action.payload;
      })
      // Cashiers - Update status (PATCH :cashier_id/status)
      .addCase(updateCashierStatus.pending, (state) => {
        state.cashiersLoading = true;
        state.cashiersError = null;
      })
      .addCase(updateCashierStatus.fulfilled, (state) => {
        state.cashiersLoading = false;
      })
      .addCase(updateCashierStatus.rejected, (state, action) => {
        state.cashiersLoading = false;
        state.cashiersError = action.payload;
      });

    // Cashier Stats
    builder
      .addCase(fetchCashierStats.pending, (state) => {
        state.cashierStatsLoading = true;
        state.cashierStatsError = null;
      })
      .addCase(fetchCashierStats.fulfilled, (state, action) => {
        state.cashierStatsLoading = false;
        const payload = action.payload;
        state.cashierStats = payload?.data || payload || null;
      })
      .addCase(fetchCashierStats.rejected, (state, action) => {
        state.cashierStatsLoading = false;
        state.cashierStatsError = action.payload;
      });

    // Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      });
  },
});

// Export actions
export const { clearError, clearSelectedCustomer, resetSellerState } = sellerSlice.actions;

// Export reducer
export default sellerSlice.reducer;